import type { FetchResult } from "@apollo/client";
import {
  CmpInvoiceDocumentEmailModeEnum,
  EmailCmpInvoiceDocumentInput,
  EmailCmpInvoiceDocumentMutation,
} from "../../../../graphql/types";
import {
  createInvoiceEmailFailureError,
  getOutboundEmailSuccessMessage,
  wasOutboundEmailSent,
} from "../../Invoicing/invoiceDeliveryFeedback";
import { runManageInvoiceActionOperation } from "../../Invoicing/ManageInvoices/manageInvoiceActionOperation";
import { UserFacingError } from "../../../../utils/userFacingError";

export type PendingEventInvoiceDocumentEmail = Readonly<{
  customerName: string;
  invoiceId: string;
  invoiceLabel: string;
  mode: CmpInvoiceDocumentEmailModeEnum;
  paymentId?: string | null;
  recipientEmail?: string | null;
}>;

export const emailSavedEventInvoiceDocument = async ({
  chamberId,
  execute,
  invoiceId,
  invoiceLabel,
  mode,
  paymentId,
}: {
  chamberId: string;
  execute: (
    input: EmailCmpInvoiceDocumentInput,
  ) => Promise<FetchResult<EmailCmpInvoiceDocumentMutation>>;
  invoiceId: string;
  invoiceLabel: string;
  mode: CmpInvoiceDocumentEmailModeEnum;
  paymentId?: string | null;
}) => {
  if (mode === CmpInvoiceDocumentEmailModeEnum.PaymentReceipt && !paymentId) {
    // Nothing was sent, so staff see this text, not an unknown-send text.
    throw new UserFacingError(
      "There is no payment on this invoice yet, so there is no receipt to email. Record a payment first.",
    );
  }

  const result = await runManageInvoiceActionOperation({
    action: "EMAIL_DOCUMENT",
    chamberId,
    execute,
    input: {
      chamberId,
      invoiceId,
      mode,
      operationId: undefined,
      paymentId: paymentId || undefined,
    },
    invoiceId,
    isAuthoritativelyCompleted: (mutationResult) =>
      !!mutationResult.data?.emailCmpInvoiceDocument
        .actionCompletedSuccessfully &&
      wasOutboundEmailSent(
        mutationResult.data.emailCmpInvoiceDocument.outboundEmail,
      ),
  });
  const payload = result.data?.emailCmpInvoiceDocument;
  const outboundEmail = payload?.outboundEmail;

  if (
    !payload?.actionCompletedSuccessfully ||
    !wasOutboundEmailSent(outboundEmail)
  ) {
    throw createInvoiceEmailFailureError({
      email: outboundEmail,
      label: invoiceLabel,
    });
  }

  const actualRecipient = outboundEmail.toEmail?.trim();

  if (!actualRecipient) {
    throw new Error("The saved email result did not include its recipient.");
  }

  return {
    successMessage: getOutboundEmailSuccessMessage({
      email: outboundEmail,
      label: invoiceLabel,
    }),
    toEmail: actualRecipient,
  };
};
